import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useIdioma } from '../../i18n/index.tsx';
import type { TelaDeModulo } from '../contrato.ts';
import { rotulo, traduzir, type Mensagem } from './i18n.ts';
import './admin.css';

type NomeId = { id: string; nome: string };
type Permissao = { codigo: string; modulo: string };
type Modulo = { slug: string; nome: string; versao: string; versao_instalada: string | null; estado: string;
  depende_de: string[]; dependentes: string[]; obrigatorio: boolean; instalado_em: string | null };
type Health = { banco: string; fila: string; worker: string; sha: string;
  fila_pendentes: number | null; fila_ativos: number | null; fila_falhados: number | null };
type PainelDTO = { jobs: { estado: string; quantidade: number }[]; outbox_pendentes: number;
  erros: { id: string; tipo: string; erro: string; criado_em: string }[] };
type TenantsDTO = { tenants: (NomeId & { plano: string; estado: string })[];
  workspaces: (NomeId & { slug: string; fuso: string; moeda: string; idioma_padrao: string })[] };
type UsuariosDTO = { usuarios: (NomeId & { email: string; ativo: boolean; papeis: NomeId[];
  empresas: NomeId[]; permissoes: Permissao[] })[];
  papeis: (NomeId & { codigo: string; permissoes: Permissao[] })[] };
type AuditoriaDTO = { itens: { id: string; criado_em: string; ator_tipo: string; ator_id: string | null;
  ator_rotulo: string | null; acao: string; entidade: string | null; registro_id: string | null;
  antes: unknown; depois: unknown }[]; proximo_cursor: string | null };
class FalhaHttp extends Error { status: number; codigo: string;
  constructor(status: number, codigo: string) { super(codigo); this.status = status; this.codigo = codigo; } }
async function requisitar<T>(url: string, corpo?: unknown): Promise<T> {
  const r = await fetch(url, { credentials: 'same-origin', ...(corpo === undefined ? {} : {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(corpo),
  }) });
  const dados = await r.json();
  if (!r.ok) throw new FalhaHttp(r.status, dados.code ?? 'erro');
  return dados as T;
}
function useTexto() { const { idioma } = useIdioma(); return (chave: Mensagem) => traduzir(idioma, chave); }
function useDados<T>(acao: string) {
  return useQuery({ queryKey: ['admin', acao], queryFn: () => requisitar<T>(`/api/admin/${acao}`) });
}
function Estado({ carregando, erro, vazio, tentar, children }: {
  carregando: boolean; erro: Error | null; vazio?: boolean; tentar: () => unknown; children: ReactNode;
}) {
  const t = useTexto();
  if (carregando) return <p role="status">{t('carregar')}</p>;
  if (erro) return <div role="alert"><p className="admin-erro">{t(erro instanceof FalhaHttp && erro.status === 403
    ? 'permissaoNegada' : erro instanceof FalhaHttp && erro.status === 422 ? 'periodoErro' : 'erro')}</p>
    <button onClick={tentar}>{t('tentar')}</button></div>;
  if (vazio) return <p role="status">{t('vazio')}</p>;
  return <>{children}</>;
}
function estadoNome(estado: string, t: ReturnType<typeof useTexto>): string {
  const nomes: Record<string, Mensagem> = { ok: 'ok', fora: 'fora', instalado: 'instalado', desinstalado: 'desinstalado',
    rodando: 'rodando', concluido: 'concluido', pendente: 'pendente', erro: 'erroJob', ativo: 'ativo', suspenso: 'inativo', cancelado: 'inativo' };
  return nomes[estado] ? t(nomes[estado]) : estado;
}
function Saude() {
  const t = useTexto();
  const q = useQuery({ queryKey: ['admin', 'health'], queryFn: () => requisitar<Health>('/api/admin/saude'), refetchInterval: 15000 });
  return <div className="admin"><p className="admin-aviso">{t('filaGlobal')}</p>
    <button onClick={() => void q.refetch()}>{t('atualizar')}</button>
    <Estado carregando={q.isPending} erro={q.error} tentar={() => q.refetch()}><div className="admin-grade">
      {(['banco', 'fila', 'worker'] as const).map(k => <article key={k}><h3>{t(k)}</h3>
        <strong className={q.data?.[k] === 'ok' ? 'admin-ok' : 'admin-erro'}>{q.data ? estadoNome(q.data[k], t) : t('desconhecido')}</strong></article>)}
      <article><h3>{t('versao')}</h3><span className="admin-mono">{q.data?.sha}</span></article>
      {(['pendentes','ativos','falhados'] as const).map(k => <article key={k}><h3>{t(k)}</h3>
        <span className="admin-numero">{q.data?.[`fila_${k}`] ?? t('desconhecido')}</span></article>)}
    </div></Estado></div>;
}
function Painel() {
  const t = useTexto(); const q = useDados<PainelDTO>('painel');
  return <div className="admin"><Saude /><p className="admin-aviso">{t('escopo')}</p>
    <Estado carregando={q.isPending} erro={q.error} tentar={() => q.refetch()}>
      <article><h2>{t('jobs')}</h2><div className="admin-grade">{q.data?.jobs.map(j => <div key={j.estado}>
        {estadoNome(j.estado,t)}: <strong>{j.quantidade}</strong></div>)}</div>
        {!q.data?.jobs.length && <p>{t('vazio')}</p>}<p>{t('outbox')}: {q.data?.outbox_pendentes}</p></article>
      <article><h2>{t('erros')}</h2>{!q.data?.erros.length && <p>{t('vazio')}</p>}
        {q.data?.erros.map(e => <p key={e.id}><time>{e.criado_em}</time> {e.tipo}<br />
          <span className="admin-mono">{e.id}</span><br />{t('falhaJob')}</p>)}</article>
    </Estado></div>;
}
function Modulos() {
  const t = useTexto(); const q = useDados<{ modulos: Modulo[] }>('modulos'); const qc = useQueryClient();
  const [confirmacao, setConfirmacao] = useState<Modulo | null>(null);
  const m = useMutation({ mutationFn: ({ slug, acao }: { slug: string; acao: string }) =>
    requisitar(`/api/admin/${acao}`, { slug }), onSuccess: async () => {
      setConfirmacao(null); await qc.invalidateQueries({ queryKey: ['admin'] });
    } });
  return <div className="admin">
    {m.error && <p role="alert" className="admin-erro">{t('operacaoErro')}</p>}
    {m.isSuccess && <p role="status">{t('operacaoOk')}</p>}
    {confirmacao && <section className="admin-confirmacao" aria-label={t('confirmar')}><h3>{confirmacao.nome}</h3>
      <p>{t('confirmarTexto')}</p><div className="admin-acoes"><button disabled={m.isPending}
        onClick={() => m.mutate({ slug: confirmacao.slug, acao: 'desinstalar' })}>{t('confirmar')}</button>
      <button disabled={m.isPending} onClick={() => setConfirmacao(null)}>{t('cancelar')}</button></div></section>}
    <Estado carregando={q.isPending} erro={q.error} vazio={q.data?.modulos.length === 0} tentar={() => q.refetch()}>
      <div className="admin-tabela"><table><thead><tr>{(['nome','estado','versao','dependencias','dependentes','acao'] as const)
        .map(k => <th key={k}>{t(k)}</th>)}</tr></thead><tbody>{q.data?.modulos.map(mod => <tr key={mod.slug}>
          <td>{mod.nome}<br /><small>{mod.slug}</small></td><td>{estadoNome(mod.estado,t)}</td>
          <td>{mod.versao_instalada ?? mod.versao}</td><td>{mod.depende_de.join(', ')}</td><td>{mod.dependentes.join(', ')}</td>
          <td>{mod.obrigatorio ? t('obrigatorio') : mod.estado === 'instalado'
            ? <button disabled={m.isPending || mod.dependentes.length > 0} onClick={() => { m.reset(); setConfirmacao(mod); }}>{t('desinstalar')}</button>
            : <button disabled={m.isPending} onClick={() => { m.reset(); m.mutate({ slug: mod.slug, acao: 'instalar' }); }}>{t('instalar')}</button>}</td>
        </tr>)}</tbody></table></div>
    </Estado></div>;
}
function Tenants() {
  const t = useTexto(); const q = useDados<TenantsDTO>('tenants');
  return <div className="admin"><p className="admin-aviso">{t('escopo')}</p>
    <Estado carregando={q.isPending} erro={q.error} vazio={q.data?.tenants.length === 0} tentar={() => q.refetch()}>
      {q.data?.tenants.map(v => <article key={v.id}><h2>{v.nome}</h2><dl><dt>{t('plano')}</dt><dd>{v.plano}</dd>
        <dt>{t('estado')}</dt><dd>{estadoNome(v.estado,t)}</dd></dl></article>)}
      <div className="admin-grade">{q.data?.workspaces.map(v => <article key={v.id}><h3>{v.nome}</h3><dl>
        <dt>{t('slug')}</dt><dd>{v.slug}</dd><dt>{t('fuso')}</dt><dd>{v.fuso}</dd><dt>{t('moeda')}</dt><dd>{v.moeda}</dd>
        <dt>{t('idioma')}</dt><dd>{v.idioma_padrao}</dd></dl></article>)}</div>
      {q.data?.workspaces.length === 0 && <p>{t('vazio')}</p>}
    </Estado></div>;
}
function Permissoes({ permissoes }: { permissoes: Permissao[] }) {
  const t = useTexto(); const modulos = [...new Set(permissoes.map(p => p.modulo))];
  return <details><summary>{t('permissoes')} ({permissoes.length})</summary>
    {modulos.map(modulo => <p key={modulo}><strong>{modulo}</strong><br />
      {permissoes.filter(p => p.modulo === modulo).map(p => p.codigo).join(', ')}</p>)}</details>;
}
function Usuarios() {
  const t = useTexto(); const q = useDados<UsuariosDTO>('usuarios');
  return <div className="admin"><p className="admin-aviso">{t('escopo')}</p>
    <Estado carregando={q.isPending} erro={q.error} vazio={q.data?.usuarios.length === 0} tentar={() => q.refetch()}>
      {q.data?.usuarios.map(u => <article key={u.id}><h2>{u.nome}</h2><dl>
        <dt>{t('identificador')}</dt><dd className="admin-mono">{u.id}</dd>
        <dt>{t('email')}</dt><dd>{u.email}</dd><dt>{t('estado')}</dt><dd>{t(u.ativo ? 'ativo' : 'inativo')}</dd>
        <dt>{t('papeis')}</dt><dd>{u.papeis.map(p => p.nome).join(', ') || t('vazio')}</dd>
        <dt>{t('empresas')}</dt><dd>{u.empresas.map(p => p.nome).join(', ') || t('vazio')}</dd></dl>
        <Permissoes permissoes={u.permissoes} /></article>)}
      <h2>{t('papeis')}</h2><div className="admin-grade">{q.data?.papeis.map(p => <article key={p.id}>
        <h3>{p.nome}</h3><Permissoes permissoes={p.permissoes} /></article>)}</div>
    </Estado></div>;
}
function Auditoria() {
  const t = useTexto(); const [filtros, setFiltros] = useState(''); const [cursor, setCursor] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['admin','auditoria',filtros,cursor], queryFn: () =>
    requisitar<AuditoriaDTO>(`/api/admin/auditoria?limite=30${filtros}${cursor ? '&cursor=' + encodeURIComponent(cursor) : ''}`) });
  return <div className="admin"><p className="admin-aviso">{t('somenteLeitura')}</p>
    <form onSubmit={e => { e.preventDefault(); const dados = new FormData(e.currentTarget); const params = new URLSearchParams();
      for (const k of ['usuario','entidade','inicio','fim']) { const v = String(dados.get(k) ?? '').trim();
        if (v) params.set(k, k === 'inicio' || k === 'fim' ? new Date(v).toISOString() : v); }
      setCursor(null); setFiltros('&' + params.toString());
    }}>{(['usuario','entidade','inicio','fim'] as const).map(k => <label key={k}>{t(k)}<input name={k}
      type={k === 'inicio' || k === 'fim' ? 'datetime-local' : 'text'} maxLength={k === 'usuario' ? 36 : 120} /></label>)}
      <button type="submit">{t('filtrar')}</button></form>
    <Estado carregando={q.isPending} erro={q.error} vazio={q.data?.itens.length === 0} tentar={() => q.refetch()}>
      <div className="admin-tabela"><table><thead><tr>{(['data','ator','acao','entidade','detalhes'] as const)
        .map(k => <th key={k}>{t(k)}</th>)}</tr></thead><tbody>{q.data?.itens.map(a => <tr key={a.id}>
          <td className="admin-mono">{a.criado_em}</td><td>{a.ator_rotulo ?? a.ator_id ?? t(a.ator_tipo === 'usuario'
            ? 'atorUsuario' : a.ator_tipo === 'chave_api' ? 'atorChave' : 'atorSistema')}</td><td>{a.acao}</td>
          <td>{a.entidade}<br /><small>{a.registro_id}</small></td><td><details><summary>{t('detalhes')}</summary>
            <h3>{t('antes')}</h3><pre>{JSON.stringify(a.antes,null,2)}</pre>
            <h3>{t('depois')}</h3><pre>{JSON.stringify(a.depois,null,2)}</pre></details></td>
        </tr>)}</tbody></table></div>
    </Estado><div className="admin-acoes"><button disabled={!cursor || q.isPending} onClick={() => setCursor(null)}>{t('primeira')}</button>
      <button disabled={!q.data?.proximo_cursor || q.isPending} onClick={() => setCursor(q.data?.proximo_cursor ?? null)}>{t('proxima')}</button></div>
  </div>;
}
export const tela: TelaDeModulo = {
  slug: 'admin', rota: '/admin', get titulo() { return rotulo('titulo'); },
  abas: [
    { id: 'painel', get rotulo() { return rotulo('painel'); }, conteudo: Painel },
    { id: 'modulos', get rotulo() { return rotulo('modulos'); }, conteudo: Modulos },
    // Tenants e workspaces escondida a pedido da dona (2026-10-07): um
    // tenant so, nada a gerir ali. A rota da API continua.
    { id: 'usuarios', get rotulo() { return rotulo('usuarios'); }, conteudo: Usuarios },
    { id: 'auditoria', get rotulo() { return rotulo('auditoria'); }, conteudo: Auditoria },
    { id: 'saude', get rotulo() { return rotulo('saude'); }, conteudo: Saude },
  ],
};
